// frontend/src/modules/site-settings/api.ts
import { api } from '../../core/api-client';

/* ──────────────── Types ──────────────── */

export interface CustomerReview {
  id: string;
  name: string;
  role: string | null;
  company: string | null;
  sector: string | null;
  city: string | null;
  rating: number | null;
  body: string;
  avatar_url: string | null;
  is_published: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}
export type CustomerReviewInput = Partial<Omit<CustomerReview, 'id' | 'created_at' | 'updated_at'>>;

export interface BlogPost {
  id: string;
  slug: string;
  title: string;
  excerpt: string | null;
  body_md: string;
  cover_url: string | null;
  author_name: string | null;
  tags: string[];
  seo_title: string | null;
  seo_description: string | null;
  og_image_url: string | null;
  is_published: boolean;
  published_at: string | null;
  created_at: string;
  updated_at: string;
}
export type BlogPostInput = Partial<Omit<BlogPost, 'id' | 'created_at' | 'updated_at'>>;

export interface SeoSettings {
  default_title: string;
  default_description: string;
  default_og_image_url: string | null;
  default_twitter_handle: string | null;
  default_robots: string;
  sitemap_enabled: boolean;
  indexable: boolean;
  gtm_id: string | null;
  ga4_id: string | null;
  updated_at: string;
}
export type SeoSettingsInput = Partial<Omit<SeoSettings, 'updated_at'>>;

export interface PlatformSettings {
  signup_enabled: boolean;
  require_admin_mfa: boolean;
  updated_at: string;
}
export type PlatformSettingsInput = Partial<Omit<PlatformSettings, 'updated_at'>>;

export interface AdminRole {
  id: string;
  key: string;
  name: string;
  description: string | null;
  is_system: boolean;
  permission_count: number;
}

export interface AdminPermission {
  key: string;
  area: string;
  description: string;
}

export interface AdminRoster {
  id: string;
  email: string;
  name: string;
  is_active: boolean;
  last_login_at: string | null;
  roles: Array<{ id: string; key: string; name: string }>;
}

/* ──────────────── Reviews ──────────────── */

export const reviewsApi = {
  list: () => api<{ reviews: CustomerReview[] }>('/api/super-admin/site/reviews').then((r) => r.reviews),
  get:  (id: string) => api<{ review: CustomerReview }>(`/api/super-admin/site/reviews/${id}`).then((r) => r.review),
  create: (input: CustomerReviewInput) =>
    api<{ review: CustomerReview }>('/api/super-admin/site/reviews', { method: 'POST', body: input }).then((r) => r.review),
  update: (id: string, input: CustomerReviewInput) =>
    api<{ review: CustomerReview }>(`/api/super-admin/site/reviews/${id}`, { method: 'PATCH', body: input }).then((r) => r.review),
  remove: (id: string) =>
    api<void>(`/api/super-admin/site/reviews/${id}`, { method: 'DELETE' }),
};

/* ──────────────── Blog ──────────────── */

export const blogApi = {
  list: () => api<{ posts: BlogPost[] }>('/api/super-admin/site/blog').then((r) => r.posts),
  get:  (id: string) => api<{ post: BlogPost }>(`/api/super-admin/site/blog/${id}`).then((r) => r.post),
  create: (input: BlogPostInput) =>
    api<{ post: BlogPost }>('/api/super-admin/site/blog', { method: 'POST', body: input }).then((r) => r.post),
  update: (id: string, input: BlogPostInput) =>
    api<{ post: BlogPost }>(`/api/super-admin/site/blog/${id}`, { method: 'PATCH', body: input }).then((r) => r.post),
  remove: (id: string) =>
    api<void>(`/api/super-admin/site/blog/${id}`, { method: 'DELETE' }),
};

/* ──────────────── SEO ──────────────── */

export const seoApi = {
  get:    () => api<{ seo: SeoSettings }>('/api/super-admin/site/seo').then((r) => r.seo),
  update: (input: SeoSettingsInput) =>
    api<{ seo: SeoSettings }>('/api/super-admin/site/seo', { method: 'PUT', body: input }).then((r) => r.seo),
};

/* ──────────────── Help docs ──────────────── */

export interface HelpDoc {
  id: string;
  slug: string;
  title: string;
  excerpt: string | null;
  body_md: string;
  category: string;
  sort_order: number;
  seo_title: string | null;
  seo_description: string | null;
  is_published: boolean;
  published_at: string | null;
  created_at: string;
  updated_at: string;
}
export type HelpDocInput = Partial<Omit<HelpDoc, 'id' | 'created_at' | 'updated_at'>>;

export const docsApi = {
  list: () => api<{ docs: HelpDoc[] }>('/api/super-admin/site/docs').then((r) => r.docs),
  get:  (id: string) => api<{ doc: HelpDoc }>(`/api/super-admin/site/docs/${id}`).then((r) => r.doc),
  create: (input: HelpDocInput) =>
    api<{ doc: HelpDoc }>('/api/super-admin/site/docs', { method: 'POST', body: input }).then((r) => r.doc),
  update: (id: string, input: HelpDocInput) =>
    api<{ doc: HelpDoc }>(`/api/super-admin/site/docs/${id}`, { method: 'PATCH', body: input }).then((r) => r.doc),
  remove: (id: string) =>
    api<void>(`/api/super-admin/site/docs/${id}`, { method: 'DELETE' }),
};

/* ──────────────── Platform settings ──────────────── */

export const platformSettingsApi = {
  get:    () => api<{ platform: PlatformSettings }>('/api/super-admin/access/platform').then((r) => r.platform),
  update: (input: PlatformSettingsInput) =>
    api<{ platform: PlatformSettings }>('/api/super-admin/access/platform', { method: 'PUT', body: input }).then((r) => r.platform),
};

/* ──────────────── Roles + admins ──────────────── */

export const accessApi = {
  listRoles:       () => api<{ roles: AdminRole[] }>('/api/super-admin/access/roles').then((r) => r.roles),
  listPermissions: () => api<{ permissions: AdminPermission[] }>('/api/super-admin/access/permissions').then((r) => r.permissions),
  rolePermissions: (roleId: string) => api<{ permissions: string[] }>(`/api/super-admin/access/roles/${roleId}/permissions`).then((r) => r.permissions),
  listAdmins:      () => api<{ admins: AdminRoster[] }>('/api/super-admin/access/admins').then((r) => r.admins),
  setAdminRoles:   (adminId: string, roleIds: string[]) =>
    api<{ admin: AdminRoster }>(`/api/super-admin/access/admins/${adminId}/roles`, { method: 'PUT', body: { role_ids: roleIds } }).then((r) => r.admin),
  inviteAdmin:     (input: { email: string; name: string; role_ids: string[] }) =>
    api<{ admin: AdminRoster; magicLinkExpiresAt: string }>('/api/super-admin/access/admins', { method: 'POST', body: input }),
  setAdminActive:  (adminId: string, isActive: boolean) =>
    api<{ admin: AdminRoster }>(`/api/super-admin/access/admins/${adminId}`, { method: 'PATCH', body: { is_active: isActive } }).then((r) => r.admin),
  // Permanent. The server refuses to remove you, the last Owner, or the last
  // admin at all; audit history survives because it keeps a denormalised label.
  deleteAdmin:     (adminId: string) =>
    api<void>(`/api/super-admin/access/admins/${adminId}`, { method: 'DELETE' }),
};

/* ──────────────── Platform announcements ──────────────── */

export type AnnouncementAudience = 'all' | 'tenant';
export type AnnouncementRecipients = 'owners' | 'all_users';

export interface Announcement {
  id: string;
  title: string;
  body: string | null;
  link: string | null;
  audience: AnnouncementAudience;
  tenant_id: string | null;
  recipients: AnnouncementRecipients;
  delivered_count: number;
  tenants_reached: number;
  sent_by_admin_id: string | null;
  sent_by_label: string | null;
  created_at: string;
}

export interface SendAnnouncementInput {
  title: string;
  body?: string | null;
  link?: string | null;
  audience: AnnouncementAudience;
  tenant_id?: string | null;
  recipients: AnnouncementRecipients;
}

export const announcementsApi = {
  list: () =>
    api<{ announcements: Announcement[] }>('/api/super-admin/announcements').then((r) => r.announcements),
  send: (input: SendAnnouncementInput) =>
    api<{ announcement: Announcement; failed_tenant_ids: string[] }>(
      '/api/super-admin/announcements', { method: 'POST', body: input },
    ),
};
